import { Alert } from '@creditornot/cb-alert';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { DocumentDropzone } from './DocumentDropzone';
import UploadedFile from './UploadedFile';
import { MAX_SIZE_BYTES, MAX_SIZE_MB, MAX_FILE_COUNT } from './constants';

type DocumentUploaderProps = {
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  onChange?: (file: File) => void;
  isUploading?: boolean;
  failedToUpload?: boolean;
  uploadedFiles: File[];
  onDeletedFile: (file: File) => void;
  fileBeingDeletedName?: string | null;
};

type UploadErrorType = 'FILE_TOO_LARGE' | 'FILE_COUNT_EXCEEDED';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  margin-block-end: 16px;
`;

const ErrorAlert = styled(Alert)`
  margin-block-start: 16px;
`;

export const DocumentUploader = ({
  inputProps,
  onChange,
  uploadedFiles,
  isUploading,
  failedToUpload,
  onDeletedFile,
  fileBeingDeletedName,
}: DocumentUploaderProps) => {
  const [showFileCountExceededError, setShowFileCountExceededError] = useState(false);
  const [uploadError, setUploadError] = useState<UploadErrorType | null>(null);
  const intl = useIntl();

  const fileIsTooLarge = uploadError === 'FILE_TOO_LARGE';
  const hasCountExceededError = uploadError === 'FILE_COUNT_EXCEEDED';

  const hasTechnicalError = failedToUpload || fileIsTooLarge;

  const maxFileCountReached = uploadedFiles.length === MAX_FILE_COUNT;

  const handleSelectFile = (file: File) => {
    if (maxFileCountReached) {
      setUploadError('FILE_COUNT_EXCEEDED');
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      setUploadError('FILE_TOO_LARGE');
      return;
    } else {
      setUploadError(null);
    }

    if (onChange) {
      onChange(file);
    }
  };

  const handleOnDelete = (file: File) => {
    onDeletedFile(file);

    if (maxFileCountReached) {
      setUploadError(null);
      return;
    }

    if (showFileCountExceededError) {
      setShowFileCountExceededError(false);
    }
  };

  const canDeleteFile = uploadedFiles.length > 1;

  return (
    <Root>
      <DocumentDropzone
        hasError={hasTechnicalError}
        onSelectFile={handleSelectFile}
        inputProps={inputProps}
        isLoading={isUploading}
      />

      {hasCountExceededError && (
        <ErrorAlert
          title={intl.formatMessage({ id: 'self-serve-sign-up.wizard.upload-page.count-exceeded-error-title' })}
          variant="error"
        >
          {intl.formatMessage(
            { id: 'self-serve-sign-up.wizard.upload-page.count-exceeded-error' },
            { count: MAX_FILE_COUNT },
          )}
        </ErrorAlert>
      )}

      {hasTechnicalError && (
        <ErrorAlert
          title={intl.formatMessage({ id: 'self-serve-sign-up.wizard.upload-page.technical-error-title' })}
          variant="error"
        >
          {intl.formatMessage({ id: 'self-serve-sign-up.wizard.upload-page.technical-error' }, { size: MAX_SIZE_MB })}
        </ErrorAlert>
      )}

      {uploadedFiles.map((file, index) => (
        <UploadedFile
          key={`${file.name}-${index}`}
          name={file.name}
          sizeInBytes={file.size}
          onDelete={canDeleteFile ? () => handleOnDelete(file) : undefined}
          isDeleting={fileBeingDeletedName === file.name}
        />
      ))}
    </Root>
  );
};
